import * as React from 'react';
import { useState, useMemo } from 'react';
import { Typography, Skeleton } from '@execonline-inc/execonline-ui';
import { DashboardSummaryPayload, RationaleItem } from '../../../DashboardSummaryStore/Types';
import MarkdownRenderer from '../../DashboardSummary/MarkdownRenderer';
import RefreshIcon from '../../Cutesies/RefreshIcon';
import CopyRoundedIcon from '../../Cutesies/CopyRoundedIcon';
import Checkmark from '../../Cutesies/Checkmark';
import ChevronDown from '../../Cutesies/ChevronDown';
import ChevronUp from '../../Cutesies/ChevronUp';
import {
  formatDateRange,
  formatGeneratedTime,
  getDefaultDateRange,
  copySummaryToClipboard,
} from '../../DashboardSummary/utils';
import { T } from '../../../Translations';

// Constants
const COPY_SUCCESS_DISPLAY_MS = 2000;

interface Props {
  summary: DashboardSummaryPayload | null;
  isLoading: boolean;
  error: string | null;
  emptyMessage?: string | null;
  onRefresh?: () => void;
}

// Known section headers to look for
const KNOWN_HEADERS = [
  'Executive Summary',
  'Key Highlights',
  'Opportunities for Continued Growth',
  'Opportunities',
  'Areas for Improvement',
  'Areas for Growth',
  'Summary',
  'Highlights',
  'Recommendations',
  'Next Steps',
  'Action Items',
];

// Pre-build regex pattern at module level to avoid recreation on each parse
const HEADER_NAMES_PATTERN = KNOWN_HEADERS.map((h) =>
  h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
).join('|');
const HEADER_PATTERN = new RegExp(
  `(?:^|\\n)(?:##\\s*(${HEADER_NAMES_PATTERN})|\\*\\*(${HEADER_NAMES_PATTERN})(?::\\s*)?\\*\\*|(${HEADER_NAMES_PATTERN}))\\s*\\n`,
  'gi',
);

// Parse markdown sections from the summary content
// Handles multiple formats: ## Header, **Header**, or plain text headers
const parseSections = (content: string): Array<{ title: string; content: string }> => {
  const sections: Array<{ title: string; content: string }> = [];

  // Reset regex lastIndex for each parse call since we're using a global pattern
  HEADER_PATTERN.lastIndex = 0;

  let match;

  // Find all headers
  const matches: Array<{ title: string; index: number; fullMatch: string }> = [];
  while ((match = HEADER_PATTERN.exec(content)) !== null) {
    const title = (match[1] || match[2] || match[3] || '').trim();
    if (title) {
      matches.push({
        title,
        index: match.index,
        fullMatch: match[0],
      });
    }
  }

  // If no headers found, return all content as a single section
  if (matches.length === 0) {
    return [{ title: 'Summary', content: content.trim() }];
  }

  // Extract content between headers
  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const nextMatch = matches[i + 1];

    // Get content from after this header to before the next header (or end of content)
    const contentStart = currentMatch.index + currentMatch.fullMatch.length;
    const contentEnd = nextMatch ? nextMatch.index : content.length;
    const sectionContent = content.slice(contentStart, contentEnd).trim();

    if (sectionContent) {
      sections.push({
        title: currentMatch.title,
        content: sectionContent,
      });
    }
  }

  // If there's content before the first header, add it as intro
  if (matches.length > 0 && matches[0].index > 0) {
    const introContent = content.slice(0, matches[0].index).trim();
    if (introContent) {
      sections.unshift({ title: 'Overview', content: introContent });
    }
  }

  return sections;
};

// AI Rationale component that auto-expands when data loads
const ExpandedAIRationale: React.FC<{ items: RationaleItem[] }> = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasItems = items.length > 0;

  // Auto-expand when items become available (adjust during render)
  const [prevHasItems, setPrevHasItems] = useState(hasItems);
  if (hasItems !== prevHasItems) {
    setPrevHasItems(hasItems);
    if (hasItems) {
      setIsExpanded(true);
    }
  }

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <button
        onClick={() => hasItems && setIsExpanded(!isExpanded)}
        className={`flex items-center space-x-2 text-sm transition-colors w-full ${
          hasItems
            ? 'text-gray-600 hover:text-gray-800 cursor-pointer'
            : 'text-gray-400 cursor-default'
        }`}
        disabled={!hasItems}
      >
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" color="currentColor" />
        ) : (
          <ChevronDown className="w-4 h-4" color="currentColor" />
        )}
        <span className="font-medium">
          <T kind="AI Rationale" />
        </span>
        {!hasItems && (
          <span className="text-gray-400 text-xs ml-2">
            <T kind="(No data available)" />
          </span>
        )}
      </button>

      {isExpanded && hasItems && (
        <div className="mt-3 space-y-4 pl-6 border-l-2 border-gray-200">
          {items.map((item, index) => (
            <div key={index} className="space-y-1">
              <Typography variant="bodyMedium" className="text-gray-700 italic">
                "{item.quote}"
              </Typography>
              <Typography variant="caption" className="text-gray-500">
                <T kind="Source:" /> {item.source}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FullAnalysisTabContent: React.FC<Props> = ({
  summary,
  isLoading,
  error,
  emptyMessage,
  onRefresh,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // All hooks must be called before any early returns to satisfy Rules of Hooks
  const defaults = getDefaultDateRange();
  const analysisContent = summary?.fullAnalysis.getOrElse(() => summary?.summary || '') || '';
  const sections = useMemo(() => parseSections(analysisContent), [analysisContent]);

  const handleCopy = async () => {
    if (!summary) return;

    // Use fullAnalysis if available, otherwise fall back to summary
    const analysisText = summary.fullAnalysis.getOrElse(() => summary.summary);
    const success = await copySummaryToClipboard(summary.headline, analysisText, summary.rationale);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), COPY_SUCCESS_DISPLAY_MS);
    }
  };

  // Show loading skeleton for both loading state and initial idle state (before data loads)
  const showLoadingSkeleton = isLoading || (!summary && !error && !emptyMessage);

  if (showLoadingSkeleton) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton variant="text" className="w-1/3 h-5" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-2/3 h-4" />
        </div>
        <div className="space-y-2">
          <Skeleton variant="text" className="w-1/3 h-5" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-full h-4" />
        </div>
        <div className="space-y-2">
          <Skeleton variant="text" className="w-1/3 h-5" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-full h-4" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        <Typography variant="bodyMedium" color="red">
          {error}
        </Typography>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium"
          >
            <T kind="Try again" />
          </button>
        )}
      </div>
    );
  }

  if (emptyMessage) {
    return (
      <div className="text-gray-500 text-sm">
        <Typography variant="bodyMedium">{emptyMessage}</Typography>
      </div>
    );
  }

  // This should not be reached due to the loading check above, but kept as safety fallback
  if (!summary) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton variant="text" className="w-1/3 h-5" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-2/3 h-4" />
        </div>
        <div className="space-y-2">
          <Skeleton variant="text" className="w-1/3 h-5" />
          <Skeleton variant="text" className="w-full h-4" />
          <Skeleton variant="text" className="w-full h-4" />
        </div>
      </div>
    );
  }

  const startDate = summary.filters.startDate.getOrElse(() => defaults.startDate);
  const endDate = summary.filters.endDate.getOrElse(() => defaults.endDate);
  const dateRange = formatDateRange(startDate, endDate);
  const generatedTime = formatGeneratedTime(summary.metadata.generatedAt);

  return (
    <div className="space-y-4">
      {/* Meta info and actions */}
      <div className="flex items-center justify-between">
        <Typography variant="caption" className="text-gray-500">
          {dateRange && `${dateRange} • `}
          {generatedTime}
        </Typography>

        {/* Action buttons */}
        <div className="flex items-center space-x-1">
          {/* Refresh button */}
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/30 rounded-lg transition-colors"
            title="Regenerate analysis"
            data-intercom-target="ai-analysis-drawer-refresh-button"
          >
            <RefreshIcon className="w-5 h-5" />
          </button>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-colors ${
              copySuccess
                ? 'text-green-600 bg-green-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/30'
            }`}
            title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
            data-intercom-target="ai-analysis-drawer-copy-button"
          >
            {copySuccess ? (
              <Checkmark className="w-5 h-5 fill-current" />
            ) : (
              <CopyRoundedIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Render all parsed sections */}
      {sections.map((section, index) => (
        <div key={index} className={`space-y-2 ${index > 0 ? 'pt-4' : ''}`}>
          <Typography variant="bodyMedium" fontWeight="semibold" color="dark">
            {section.title}
          </Typography>
          <MarkdownRenderer content={section.content} expandable={false} />
        </div>
      ))}

      {/* AI Rationale section - auto-expands when data loads */}
      <ExpandedAIRationale items={summary.rationale.getOrElse(() => [])} />
    </div>
  );
};

export default FullAnalysisTabContent;
